import * as THREE from 'three'

import { UVBuilder } from '../uv-builder'
import {
  artworkBaseIndex,
  mountBackBaseIndex,
  mountFrontInnerBaseIndex,
  mountFrontOuterBaseIndex,
} from './vertice'

/**
 * Gets an array of mount UVs.
 */
const getPassepartoutUvs = (
  builder: UVBuilder,
  passepartoutGeometry: THREE.Geometry,
) => {
  return passepartoutGeometry.faces.map((face) => {
    return builder.planar('z', [face.a, face.b, face.c]).toUv()
  })
}

/**
 * Gets an array of mount core UVs.
 */
const getPassepartoutCoreUvs = (
  builder: UVBuilder,
  passepartoutCoreGeometry: THREE.Geometry,
) => {
  return passepartoutCoreGeometry.faces.map((face) => {
    return builder.planar('z', [face.a, face.b, face.c]).toUv()
  })
}

/**
 * Gets an array of mount faces.
 * All faces are laid out in ring fashion, following CSS TRBL order.
 */
const getPassepartoutFaces = () => {
  return [
    // Front-facing
    // Top
    [
      mountFrontOuterBaseIndex + 0,
      mountFrontInnerBaseIndex + 1,
      mountFrontOuterBaseIndex + 1,
    ],
    [
      mountFrontOuterBaseIndex + 0,
      mountFrontInnerBaseIndex + 0,
      mountFrontInnerBaseIndex + 1,
    ],
    // Right
    [
      mountFrontOuterBaseIndex + 1,
      mountFrontInnerBaseIndex + 2,
      mountFrontOuterBaseIndex + 2,
    ],
    [
      mountFrontOuterBaseIndex + 1,
      mountFrontInnerBaseIndex + 1,
      mountFrontInnerBaseIndex + 2,
    ],
    // Bottom
    [
      mountFrontOuterBaseIndex + 2,
      mountFrontInnerBaseIndex + 3,
      mountFrontOuterBaseIndex + 3,
    ],
    [
      mountFrontOuterBaseIndex + 2,
      mountFrontInnerBaseIndex + 2,
      mountFrontInnerBaseIndex + 3,
    ],
    // Left
    [
      mountFrontOuterBaseIndex + 3,
      mountFrontInnerBaseIndex + 0,
      mountFrontOuterBaseIndex + 0,
    ],
    [
      mountFrontOuterBaseIndex + 3,
      mountFrontInnerBaseIndex + 3,
      mountFrontInnerBaseIndex + 0,
    ],
  ].map(
    (face) =>
      new THREE.Face3(
        face[0] - mountFrontOuterBaseIndex,
        face[1] - mountFrontOuterBaseIndex,
        face[2] - mountFrontOuterBaseIndex,
      ),
  )
}

/**
 * Gets an array of mount core faces.
 * All faces are laid out in ring fashion, following CSS TRBL order.
 */
const getPassepartoutCoreFaces = () => {
  return [
    // Out-facing
    // Top
    [
      mountBackBaseIndex + 0,
      mountFrontOuterBaseIndex + 1,
      mountBackBaseIndex + 1,
    ],
    [
      mountFrontOuterBaseIndex + 0,
      mountFrontOuterBaseIndex + 1,
      mountBackBaseIndex + 0,
    ],
    // Right
    [
      mountBackBaseIndex + 1,
      mountFrontOuterBaseIndex + 2,
      mountBackBaseIndex + 2,
    ],
    [
      mountBackBaseIndex + 1,
      mountFrontOuterBaseIndex + 1,
      mountFrontOuterBaseIndex + 2,
    ],
    // Bottom
    [
      mountBackBaseIndex + 2,
      mountFrontOuterBaseIndex + 3,
      mountBackBaseIndex + 3,
    ],
    [
      mountBackBaseIndex + 2,
      mountFrontOuterBaseIndex + 2,
      mountFrontOuterBaseIndex + 3,
    ],
    // Left
    [
      mountBackBaseIndex + 0,
      mountBackBaseIndex + 3,
      mountFrontOuterBaseIndex + 0,
    ],
    [
      mountFrontOuterBaseIndex + 0,
      mountBackBaseIndex + 3,
      mountFrontOuterBaseIndex + 3,
    ],

    // In-facing
    // Top
    [
      mountFrontInnerBaseIndex + 0,
      artworkBaseIndex + 1,
      mountFrontInnerBaseIndex + 1,
    ],
    [mountFrontInnerBaseIndex + 0, artworkBaseIndex + 0, artworkBaseIndex + 1],
    // Right
    [
      mountFrontInnerBaseIndex + 1,
      artworkBaseIndex + 2,
      mountFrontInnerBaseIndex + 2,
    ],
    [mountFrontInnerBaseIndex + 1, artworkBaseIndex + 1, artworkBaseIndex + 2],
    // Bottom
    [
      mountFrontInnerBaseIndex + 2,
      artworkBaseIndex + 3,
      mountFrontInnerBaseIndex + 3,
    ],
    [mountFrontInnerBaseIndex + 2, artworkBaseIndex + 2, artworkBaseIndex + 3],
    // Left
    [mountFrontInnerBaseIndex + 3, artworkBaseIndex + 3, artworkBaseIndex + 0],
    [
      mountFrontInnerBaseIndex + 3,
      artworkBaseIndex + 0,
      mountFrontInnerBaseIndex + 0,
    ],
  ].map(
    (face) =>
      new THREE.Face3(
        face[0] - mountFrontOuterBaseIndex,
        face[1] - mountFrontOuterBaseIndex,
        face[2] - mountFrontOuterBaseIndex,
      ),
  )
}

/**
 * Generates mount geometry.
 */
export const generatePassepartoutGeometry = (
  passepartoutGeometry: THREE.Geometry,
  vertices: THREE.Vector3[],
) => {
  passepartoutGeometry.vertices.push(
    ...vertices.slice(mountFrontOuterBaseIndex, mountFrontOuterBaseIndex + 16),
  )

  const passepartoutFaces = getPassepartoutFaces()
  passepartoutGeometry.faces.push(...passepartoutFaces)

  passepartoutGeometry.computeFaceNormals()
  passepartoutGeometry.computeBoundingBox()
}

/**
 * Generates mount core geometry.
 */
export const generatePassepartoutCoreGeometry = (
  passepartoutCoreGeometry: THREE.Geometry,
  vertices: THREE.Vector3[],
) => {
  passepartoutCoreGeometry.vertices.push(
    ...vertices.slice(mountFrontOuterBaseIndex, mountFrontOuterBaseIndex + 16),
  )

  const passepartoutCoreFaces = getPassepartoutCoreFaces()
  passepartoutCoreGeometry.faces.push(...passepartoutCoreFaces)

  passepartoutCoreGeometry.computeFaceNormals()
  passepartoutCoreGeometry.computeBoundingBox()
}

/**
 * Updates mount geometry.
 */
export const updatePassepartoutGeometry = (
  passepartoutGeometry: THREE.Geometry,
  vertices: THREE.Vector3[],
) => {
  vertices
    .slice(mountFrontOuterBaseIndex, mountFrontOuterBaseIndex + 16)
    .forEach((vertice, index) => {
      passepartoutGeometry.vertices[index].set(vertice.x, vertice.y, vertice.z)
    })

  passepartoutGeometry.verticesNeedUpdate = true
  passepartoutGeometry.elementsNeedUpdate = true
  passepartoutGeometry.computeFaceNormals()
  passepartoutGeometry.computeBoundingBox()
}

/**
 * Updates mount geometry.
 */
export const updatePassepartoutCoreGeometry = (
  passepartoutCoreGeometry: THREE.Geometry,
  vertices: THREE.Vector3[],
) => {
  vertices
    .slice(mountFrontOuterBaseIndex, mountFrontOuterBaseIndex + 16)
    .forEach((vertice, index) => {
      passepartoutCoreGeometry.vertices[index].set(
        vertice.x,
        vertice.y,
        vertice.z,
      )
    })

  passepartoutCoreGeometry.verticesNeedUpdate = true
  passepartoutCoreGeometry.elementsNeedUpdate = true
  passepartoutCoreGeometry.computeFaceNormals()
  passepartoutCoreGeometry.computeBoundingBox()
}

/**
 * Generates mount UVs.
 */
export const generatePassepartoutUvs = (
  passepartoutGeometry: THREE.Geometry,
) => {
  const builder = new UVBuilder(passepartoutGeometry)
  const passepartoutUvs = getPassepartoutUvs(builder, passepartoutGeometry)

  passepartoutGeometry.faceVertexUvs = [[...passepartoutUvs]]
  passepartoutGeometry.uvsNeedUpdate = true
  passepartoutGeometry.elementsNeedUpdate = true
}

/**
 * Generates mount core UVs.
 */
export const generatePassepartoutCoreUvs = (
  passepartoutCoreGeometry: THREE.Geometry,
) => {
  const builder = new UVBuilder(passepartoutCoreGeometry)
  const passepartoutCoreUvs = getPassepartoutCoreUvs(
    builder,
    passepartoutCoreGeometry,
  )

  passepartoutCoreGeometry.faceVertexUvs = [[...passepartoutCoreUvs]]
  passepartoutCoreGeometry.uvsNeedUpdate = true
  passepartoutCoreGeometry.elementsNeedUpdate = true
}

/**
 * Updates mount UVs.
 */
export const updatePassepartoutUvs = (passepartoutGeometry: THREE.Geometry) => {
  const builder = new UVBuilder(passepartoutGeometry)
  const passepartoutUvs = getPassepartoutUvs(builder, passepartoutGeometry)

  passepartoutGeometry.faceVertexUvs = [[...passepartoutUvs]]
  passepartoutGeometry.uvsNeedUpdate = true
  passepartoutGeometry.elementsNeedUpdate = true
}

/**
 * Updates mount core UVs.
 */
export const updatePassepartoutCoreUvs = (
  passepartoutCoreGeometry: THREE.Geometry,
) => {
  const builder = new UVBuilder(passepartoutCoreGeometry)
  const passepartoutCoreUvs = getPassepartoutCoreUvs(
    builder,
    passepartoutCoreGeometry,
  )

  passepartoutCoreGeometry.faceVertexUvs = [[...passepartoutCoreUvs]]
  passepartoutCoreGeometry.uvsNeedUpdate = true
  passepartoutCoreGeometry.elementsNeedUpdate = true
}
